import { FunctionComponent, useContext, useState } from "react";
import { CreateOtherProductType, ProductTypeEnum } from "types/products/ProductCommandsType";
import AppStateContext from "contexts/AppStateContext";
import { apiCreateProduct } from "util/network/Products";
import { apiAddCartItem } from "util/network/Carts";
import { apiCreateFileResource } from "util/network/FileResources";
import {
  Badge,
  IconButton,
  SvgIcon,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { Check, Close, FileUpload } from "@mui/icons-material";
import { useToast } from "contexts/ToastContext";
import styles from "./Styles/cartTableRowStyle.module.css";
import AmountInput from "./AmountInput";
import EditableAdornment from "./EditableAdornment";
import Modal from "components/Modals/Modal";

type PropsType = {
  cartId: number;
  onCancel: () => void;
  onProductCreated: () => void;
};

const AddOtherProductTableRow: FunctionComponent<PropsType> = ({
  cartId,
  onCancel,
  onProductCreated,
}) => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();

  const [name, setName] = useState<string>("New product");
  const [nameDraft, setNameDraft] = useState<string>("New product");
  const [qty, setQty] = useState<number>(1);
  const [price, setPrice] = useState<number>(0);
  const [priceText, setPriceText] = useState<string>("0");
  const [weight, setWeight] = useState<number>(0);
  const [weightText, setWeightText] = useState<string>("0");
  const [description, setDescription] = useState<string>("");
  const [descriptionDraft, setDescriptionDraft] = useState<string>("");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showFileModal, setShowFileModal] = useState<boolean>(false);

  const handleAddFiles = (files: File[]) => {
    setPendingFiles((prev) => [...prev, ...files]);
  };

  const handleRemoveFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveName = () => {
    if (nameDraft !== name) {
      setName(nameDraft);
    }
  };

  const handleDiscardName = () => {
    setNameDraft(name);
  };

  const handleSaveDescription = () => {
    if (descriptionDraft !== description) {
      setDescription(descriptionDraft);
    }
  };

  const handleDiscardDescription = () => {
    setDescriptionDraft(description);
  };

  const handleSavePrice = () => {
    const parsed = parseFloat(priceText.trim().replace(",", "."));
    if (isNaN(parsed)) {
      setPriceText(String(price));
      return;
    }
    setPrice(parsed);
  };

  const handleDiscardPrice = () => {
    setPriceText(String(price));
  };

  const handleSaveWeight = () => {
    const parsed = parseFloat(weightText.trim().replace(",", "."));
    if (isNaN(parsed)) {
      setWeightText(String(weight));
      return;
    }
    setWeight(parsed);
  };

  const handleDiscardWeight = () => {
    setWeightText(String(weight));
  };

  const handleSave = async () => {
    if (!token) return;
    setIsSaving(true);

    try {
      const uploadedResources = await Promise.all(
        pendingFiles.map((file) => apiCreateFileResource(token, file))
      );

      const command: CreateOtherProductType = {
        name: name,
        price: price,
        weight: weight,
        description: description,
        fileResources: uploadedResources.map((x) => x.id),
        productType: ProductTypeEnum.OTHER,
      };

      const productResult = await apiCreateProduct(token, command);

      await apiAddCartItem(token, cartId, productResult.id, qty);

      addToast({
        type: "success",
        message: "Other product added successfully",
        keep: false,
      });
      onProductCreated();
    } catch (error) {
      addToast({
        type: "error",
        message: "Failed to add other product: " + error,
        keep: true,
      });
      setIsSaving(false);
    }
  };

  return (
    <TableRow className={styles.cellsNoWrap} sx={{ backgroundColor: "#e3f2fd" }}>
      <TableCell align="left" sx={{ padding: 0 }}>
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <span>&nbsp;</span>
        </div>
      </TableCell>

      <TableCell>
        <TextField
          value={nameDraft}
          onChange={(e) => setNameDraft(e.target.value)}
          size="small"
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSaveName();
            if (e.key === "Escape") handleDiscardName();
          }}
          InputProps={{
            endAdornment: (
              <EditableAdornment
                isDirty={nameDraft !== name}
                onSave={handleSaveName}
                onDiscard={handleDiscardName}
              />
            ),
          }}
        />
      </TableCell>

      <TableCell colSpan={2}>
        <TextField
          sx={{ width: "90%" }}
          value={descriptionDraft}
          onChange={(e) => setDescriptionDraft(e.target.value)}
          fullWidth
          size="small"
          multiline
          rows={2}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) handleSaveDescription();
            if (e.key === "Escape") handleDiscardDescription();
          }}
          InputProps={{
            endAdornment: (
              <EditableAdornment
                isDirty={descriptionDraft !== description}
                onSave={handleSaveDescription}
                onDiscard={handleDiscardDescription}
              />
            ),
          }}
        />
      </TableCell>

      <TableCell>
        <TextField
          value={weightText}
          onChange={(e) => setWeightText(e.target.value)}
          type="number"
          fullWidth
          size="small"
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSaveWeight();
            if (e.key === "Escape") handleDiscardWeight();
          }}
          sx={{ width: weightText !== String(weight) ? "20ch" : "12ch" }}
          InputProps={{
            endAdornment: (
              <EditableAdornment
                isDirty={weightText !== String(weight)}
                onSave={handleSaveWeight}
                onDiscard={handleDiscardWeight}
                defaultAdornment="g"
              />
            ),
          }}
        />
      </TableCell>

      <TableCell>
        <Tooltip title={<h3>UPLOAD FILES</h3>}>
          <IconButton onClick={() => setShowFileModal(true)}>
            <Badge badgeContent={pendingFiles.length} color="primary">
              <SvgIcon><FileUpload /></SvgIcon>
            </Badge>
          </IconButton>
        </Tooltip>
        <Modal isOpen={showFileModal} onClose={() => setShowFileModal(false)}>
          <div style={{ height: "100%", overflow: "auto" }}>
            <label>
              <input
                type="file"
                multiple
                onChange={(e) => {
                  if (e.target.files) handleAddFiles(Array.from(e.target.files));
                  e.target.value = "";
                }}
              />
            </label>
            <ul>
              {pendingFiles.map((file, index) => (
                <li key={index}>
                  <span>{file.name}</span>
                  <button onClick={() => handleRemoveFile(index)}>Remove</button>
                </li>
              ))}
            </ul>
          </div>
        </Modal>
      </TableCell>

      <TableCell align="center">
        <AmountInput
          initVal={qty}
          onChange={setQty}
          direction="column-reverse"
        />
      </TableCell>

      <TableCell align="center">
        <TextField
          value={priceText}
          onChange={(e) => setPriceText(e.target.value)}
          type="number"
          fullWidth
          size="small"
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSavePrice();
            if (e.key === "Escape") handleDiscardPrice();
          }}
          sx={{ m: 1, width: "19ch" }}
          InputProps={{
            endAdornment: (
              <EditableAdornment
                isDirty={priceText !== String(price)}
                onSave={handleSavePrice}
                onDiscard={handleDiscardPrice}
                defaultAdornment="kr"
              />
            ),
          }}
        />
      </TableCell>

      <TableCell align="right" colSpan={2}>
        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
          <Tooltip title={<h5>GEM</h5>} placement="bottom">
            <IconButton
              size="small"
              color="success"
              onClick={handleSave}
              disabled={isSaving}
            >
              <Check fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={<h5>SLET</h5>} placement="bottom">
            <IconButton
              size="small"
              color="error"
              onClick={onCancel}
              disabled={isSaving}
            >
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </TableCell>
    </TableRow>
  );
};

export default AddOtherProductTableRow;
